package com.exura.components.layouts

import androidx.compose.runtime.Composable
import com.exura.components.dialogs.DialogController
import com.exura.components.dialogs.DialogPanelKind
import com.exura.components.snackbar.SnackbarController
import com.exura.features.newauth.domain.manager.AuthManager
import com.exura.features.newauth.domain.model.AuthState
import com.exura.features.newauth.domain.model.ProviderAuthUser
import com.exura.features.userprofile.domain.model.ProviderProfile
import com.exura.features.userprofile.domain.usecase.GetProviderProfileUseCase
import com.varabyte.kobweb.silk.style.CssStyleVariant
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.update

class NavigableLayoutScope(
    private val authManager: AuthManager,
    private val controller: DialogController,
    private val snackbarController: SnackbarController,
    private val getProfileUseCase: GetProviderProfileUseCase,

) {
    private val refreshTrigger = MutableStateFlow(0)

    fun refreshProfile() {
        refreshTrigger.update { it + 1 }
    }

    fun showSnackbar(message: String) {
        snackbarController.show(message)
    }

    suspend fun showSnackbarSuspend(message: String) {
        snackbarController.showAwaitable(message)
    }

    fun showDialog(
        variant: CssStyleVariant<DialogPanelKind>? = null,
        content: @Composable () -> Unit,
    ) {
        controller.show(variant, content)
    }

    fun closeDialog() {
        controller.close()
    }

    fun observeUser() : Flow<ProviderAuthUser?> = authManager.state
        .map { state ->
            if (state is AuthState.Authenticated && state.user is ProviderAuthUser) {
                state.user as ProviderAuthUser
            } else {
                null
            }
        }
        .distinctUntilChanged()

    @OptIn(ExperimentalCoroutinesApi::class)
    fun observeProfile(): Flow<ProviderProfile?> {
        return combine(
            authManager.state
                .map { state -> (state as? AuthState.Authenticated)?.user?.id }
                .distinctUntilChanged(),
            refreshTrigger
        ) { userId, _ -> userId }
            .flatMapLatest { userId ->
                if (userId == null) {
                    flowOf(null)
                } else {
                    // When User ID changes OR refresh is triggered, we fetch fresh data
                    flow {
                        emit(getProfileUseCase().getOrNull())
                    }
                }
            }
    }

    suspend fun profile() : Result<ProviderProfile?> = getProfileUseCase()
}
